import { formatDisplayDateTime } from '@/utils/date';
import { formatTaka } from '@/utils/number';
import type { PcmpAccountsAttempt } from '../../types/pcmpAccounts.types';
import type { PcmpQueuePerson } from '../../types/pcmpQueue.types';
import { PcmpEmptyCell } from '../PcmpQueue/PcmpQueueCells';

function ByAt({ by, at }: { by: PcmpQueuePerson | null; at: string | null }) {
  if (!at) return null;
  return (
    <span className="truncate text-xs text-muted-foreground" title={by?.fullName}>
      {[by?.fullName, formatDisplayDateTime(at)].filter(Boolean).join(' · ')}
    </span>
  );
}

interface PcmpFailureSummaryProps {
  stageLabel: string | null;
  reason: string | null;
  by: PcmpQueuePerson | null;
  at: string | null;
}

/** A failed payout: its stage, reason and who marked it when. */
export function PcmpFailureSummary({ stageLabel, reason, by, at }: PcmpFailureSummaryProps) {
  return (
    <div className="flex max-w-64 flex-col">
      <span className="font-medium text-destructive">
        Failed{stageLabel ? ` · ${stageLabel}` : ''}
      </span>
      {reason ? (
        <span className="line-clamp-2 text-sm" title={reason}>
          {reason}
        </span>
      ) : null}
      <ByAt by={by} at={at} />
    </div>
  );
}

/** The latest payout attempt: paid, failed (with stage and reason) or returned. */
export function PcmpAccountsAttemptCell({ attempt }: { attempt: PcmpAccountsAttempt | null }) {
  if (!attempt) return <PcmpEmptyCell />;
  switch (attempt.result) {
    case 'PAID':
      return (
        <div className="flex max-w-64 flex-col">
          <span className="whitespace-nowrap tabular-nums">Paid {formatTaka(attempt.amount)}</span>
          <ByAt by={attempt.paidBy} at={attempt.paidAt} />
        </div>
      );
    case 'FAILED':
      return (
        <PcmpFailureSummary
          stageLabel={attempt.failureStageLabel}
          reason={attempt.failureReason}
          by={attempt.failedBy}
          at={attempt.failedAt}
        />
      );
    case 'RETURNED':
      return (
        <div className="flex max-w-64 flex-col">
          <span className="font-medium">Returned</span>
          {attempt.returnReason ? (
            <span className="line-clamp-2 text-sm" title={attempt.returnReason}>
              {attempt.returnReason}
            </span>
          ) : null}
          <ByAt by={attempt.returnedBy} at={attempt.returnedAt} />
        </div>
      );
    default:
      return <span className="text-muted-foreground">Awaiting payout</span>;
  }
}
